import { Form } from "antd"
import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link } from "react-router-dom"

import Images from "../../Images"
import { TextInput } from "../Input/TextInput.tsx"
import { normalizeEmail } from "../../utils/form/normalizeEmail"
import utils from "../../utils/utils"
import ButtonComponent from "../Button"
import Fade from "../Fade"

const formImages = Images as typeof Images & { email: string }

type FormErrorText = string | string[]

interface AddEmailFormValues {
  email: string
  accept?: string
  confirm?: string
  [key: string]: unknown
}

interface AddEmailFormProps {
  error?: string | Record<string, FormErrorText> | null
  onSubmit: (values: AddEmailFormValues) => void
}

function AddEmailForm({ error, ...props }: AddEmailFormProps) {
  const [errorText, setErrorText] = useState<FormErrorText | null>(null)

  const [isVisibleBtn, setIsVisibleBtn] = useState(false)
  const [isBackError, setIsBackError] = useState(false)

  const [form] = Form.useForm()
  const { t } = useTranslation()

  useEffect(() => {
    if (error) {
      if (typeof error === "string") {
        if (error.length > 100) {
          setErrorText(t("Something went wrong, please try later again"))
        } else {
          setErrorText(error)
        }
      } else {
        setErrorText(Object.values(error)[0])
      }
      setIsBackError(true)
    }
  }, [error])

  const onFinish = (values: AddEmailFormValues) => {
    const data = { ...values }
    delete data.accept
    delete data.confirm
    props.onSubmit(values)
  }

  const onFinishFailed = ({
    errorFields
  }: {
    errorFields?: Array<{ errors: string[] }>
  }) => {
    if (errorFields) setErrorText(errorFields[0]["errors"])
  }

  function handleClearError(
    e: React.ChangeEvent<HTMLInputElement>,
    field: string
  ) {
    form.setFields([
      {
        name: field,
        errors: []
      }
    ])
    let newError: FormErrorText | null = null
    const errorList = form.getFieldsError()

    errorList.forEach((field) => {
      if (field.errors.length && !newError) newError = field.errors[0]
    })
    setIsBackError(false)
    setErrorText(newError)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target
    if (name === "email") {
      if (utils.validateEmail(value)) {
        if (!isVisibleBtn) setIsVisibleBtn(true)
      } else {
        if (isVisibleBtn) setIsVisibleBtn(false)
      }
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger={["onSubmit", "onChange"]}
      className="login-form"
      layout="vertical"
    >
      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div
            className={clsx("error-text", { "error-text_empty": !errorText })}
          >
            {isBackError ? (
              <>
                {`${errorText ?? ""} ${t("Kindly try another one or")} `}{" "}
                <Link to="/login">{t("Sign In")}</Link>
              </>
            ) : (
              errorText
            )}
          </div>
        )}
      </Form.Item>

      <Form.Item
        label=""
        name="email"
        validateStatus={isBackError ? "error" : undefined}
        rules={[
          { required: true, message: t("Please insert your Email") },
          { type: "email", message: t("The email you entered is incorrect") }
        ]}
        style={{ marginBottom: 0 }}
        normalize={normalizeEmail}
      >
        <TextInput
          disableAutoCapitalize
          placeholder={t("Email address")}
          name="email"
          type="email"
          onChange={(e) => {
            handleChange(e)
            handleClearError(e, "email")
          }}
          prefix={<img src={formImages.email} alt="" />}
        />
      </Form.Item>

      <Fade inProp={isVisibleBtn} style={{ order: 1, marginTop: "auto" }}>
        <ButtonComponent
          type="primary"
          htmlType="submit"
          disabled={!isVisibleBtn}
          block
        >
          {t("Create Account")}
        </ButtonComponent>
      </Fade>
    </Form>
  )
}

export default AddEmailForm
